import { WIN_LABEL_STYLE } from '@/containers/common/TextStyles'
import { TweenUtils } from '@/utils/TweenUtils'
import { toMoney } from '@base/utils/Formatter'
import gsap from 'gsap'
import * as PIXI from 'pixi.js'
import { ScalingTextField } from '@/containers/common/ScalingTextField'
import { toMajorUnits } from '@/utils/CurrencyUnits'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import { toCurrency } from '@/ui/currency'
import Facade from '@/Facade'
import { WinCounter } from '@/managers/winning/WinCounter'

const WIN_LABEL_BACKGROUND_ASSET = 'win_line'
const WIN_LABEL_MAX_WIDTH = 350
export const TOTAL_WIN_COUNTUP_DURATION = WinCounter.DURATION

export type WinLabelVariant = 'line_win' | 'total_win'

export interface IWinLabelVariant {
    in: string
    loop: string
    out: string
    backgroundScale: number
    fontSize: number
}

export const WIN_LABEL_VARIANTS: Record<WinLabelVariant, IWinLabelVariant> = {
    line_win: {
        in: 'small_in',
        loop: 'small_loop',
        out: 'small_out',
        backgroundScale: 1,
        fontSize: 50,
    },
    total_win: {
        in: 'big_in',
        loop: 'big_loop',
        out: 'big_out',
        backgroundScale: 0.75,
        fontSize: 100,
    },
}

const WIN_LABEL_DEFAULT_VARIANT: WinLabelVariant = 'line_win'

export class WinLabel extends PIXI.Container {
    private readonly _background: SpineAnimation
    private readonly _valueTxt: ScalingTextField
    private _isActive: boolean
    private _variant: WinLabelVariant
    private _countTween: gsap.core.Tween | null

    public constructor() {
        super()

        this._background = new SpineAnimation()
        this._background.init({ name: WIN_LABEL_BACKGROUND_ASSET })
        this._background.visible = false
        this.addChild(this._background)

        this._valueTxt = new ScalingTextField(1, WIN_LABEL_MAX_WIDTH, PIXI.Text, WIN_LABEL_STYLE)
        this.addChild(this._valueTxt)

        this._isActive = false
        this._variant = WIN_LABEL_DEFAULT_VARIANT
        this._countTween = null
        this.applyVariant(WIN_LABEL_DEFAULT_VARIANT)
    }

    public init = () => {
        this.alpha = 1
        this._isActive = false
        this.killCountTween()
    }

    public reset = () => {
        this.killCountTween()
        this.stopBackground()
        this.applyVariant(WIN_LABEL_DEFAULT_VARIANT)
    }

    public setVariant(variant: WinLabelVariant): void {
        if (this._variant === variant) {
            return
        }

        this.applyVariant(variant)
    }

    private applyVariant(variant: WinLabelVariant): void {
        this._variant = variant

        const config = WIN_LABEL_VARIANTS[variant]

        this._background.scale.set(config.backgroundScale)
        ;(this._valueTxt.content as PIXI.Text).style.fontSize = config.fontSize

        // ScalingTextField re-fits inside its text setter, so re-assign to pick up the new size.
        this._valueTxt.text = this._valueTxt.text
    }

    private formatWinAmount(value: number): string {
        const currency = toCurrency(String(Facade.model?.initData?.player?.currency ?? ''))
        return `${currency}${toMoney(value, 2)}`
    }

    public changeValue(value: number) {
        if (this._isActive) {
            return
        }

        this.killCountTween()
        this._valueTxt.text = this.formatWinAmount(toMajorUnits(value))
    }

    /**
     * Smooth count-up for Total Line Win. Starts at 0 major units and tweens to the target.
     * Safe to call before or together with showLabel (does not require inactive state).
     */
    public countUpValue(value: number, duration: number = TOTAL_WIN_COUNTUP_DURATION): void {
        this.killCountTween()

        const target = Math.max(0, toMajorUnits(value))
        const counter = { value: 0 }
        this._valueTxt.text = this.formatWinAmount(0)

        if (target <= 0 || duration <= 0) {
            this._valueTxt.text = this.formatWinAmount(target)
            return
        }

        this._countTween = gsap.to(counter, {
            duration,
            value: target,
            ease: WinCounter.EASING,
            onUpdate: () => {
                this._valueTxt.text = this.formatWinAmount(counter.value)
            },
            onComplete: () => {
                this._valueTxt.text = this.formatWinAmount(target)
                this._countTween = null
            },
        })
    }

    public showLabel() {
        if (this._isActive) {
            return
        }

        this.playBackgroundIn()
        TweenUtils.fadeIn(this, 0.5)
        this._isActive = true
    }

    /** Reuse the same label between line-by-line steps (avoids Spine.from every payline). */
    public showLineWin(value: number): void {
        gsap.killTweensOf(this)
        this.killCountTween()
        this.alpha = 1
        this.visible = true
        this._isActive = false
        this.setVariant('line_win')
        this.changeValue(value)
        this.showLabel()
    }

    public hideLabel(onComplete?: Function, duration: number = 1) {
        this.killCountTween()

        if (!this._isActive) {
            if (onComplete) {
                onComplete()
            }
            return
        }

        this.playBackgroundOut()
        TweenUtils.fadeOut(this, duration)

        gsap.delayedCall(duration, () => {
            this._isActive = false
            this.visible = false
            this.stopBackground()
            if (onComplete) {
                onComplete()
            }
        })
    }

    private killCountTween(): void {
        if (!this._countTween) {
            return
        }

        this._countTween.kill()
        this._countTween = null
    }

    //## Total win and Line wins.
    private playBackgroundIn(): void {
        if (!this._background.animation) {
            return
        }

        const config = WIN_LABEL_VARIANTS[this._variant]

        this._background.visible = true
        this._background.stop()
        this._background.start([config.in, config.loop], [false, true], 0, [1, 1])
    }

    private playBackgroundOut(): void {
        if (!this._background.animation) {
            return
        }

        this._background.start([WIN_LABEL_VARIANTS[this._variant].out], [false], 0, [1])
    }

    private stopBackground(): void {
        if (!this._background.animation) {
            return
        }

        this._background.stop()
        this._background.visible = false
    }
}
